import { Box, IconButton } from "@mui/material";
import { alpha } from "@mui/material/styles";
import classNames from "classnames";
import * as React from "react";

import { RobotoTooltip } from "@/shared/components/RobotoTooltip";
import { CloseIcon, SettingsIcon } from "@/shared/foundation/icons/mui";
import { useDebouncedCallback } from "@/shared/hooks";
import {
  type PlotPanelState,
  actions,
  VizStateDispatchContext,
} from "@/shared/state/visualization";
import { LegendPosition } from "@/shared/state/visualization/schema";
import type { XAxisAlignment } from "@/shared/state/visualization/schema/v1";

import { OpenPanelSettings } from "../../controls";
import type { PlotPanelSettingsSidebarProps } from "../../PanelSettings/PlotPanelSettings";
import { SidebarViewType, useWorkspaceSidebar } from "../../sidebarContext";
import {
  useEphemeralWorkspaceState,
  useWorkspaceFiles,
} from "../../WorkspaceCtx/hooks";

import type { SeriesAlignment } from "./alignment";
import styles from "./Legend.module.css";
import { usePlotPanelContext } from "./panelContext";
import type { Extents } from "./PlotRenderer";
import { seriesDisplayLabels } from "./seriesDisplayLabels";

interface LegendProps {
  state: PlotPanelState;
  /**
   * The extents of the chart as drawn on its canvas. Used to anchor the
   * bottom legends above the chart's bottom (x-axis) pixel margin, which keeps
   * them clear of the x-axis label band and lifts the bottom-right legend above
   * the "Fit to Data" button that shares that corner and is offset from the
   * panel bottom by the same margin.
   */
  chartExtents?: Extents | null;
  /**
   * How each series plots in aligned mode; `undefined` when the panel plots
   * absolute time. The legend flags series whose anchor could not be
   * resolved (see {@link SeriesAlignment}).
   */
  alignment?: SeriesAlignment;
}

const positionClassMap: Record<LegendPosition, string> = {
  [LegendPosition.TopLeft]: styles.topLeft,
  [LegendPosition.TopRight]: styles.topRight,
  [LegendPosition.BottomLeft]: styles.bottomLeft,
  [LegendPosition.BottomRight]: styles.bottomRight,
};

export function Legend({ state, chartExtents, alignment }: LegendProps) {
  const vizStateDispatch = React.useContext(VizStateDispatchContext);
  const { isLegendShown } = usePlotPanelContext();
  const workspaceFiles = useWorkspaceFiles();

  const seriesLabels = React.useMemo(
    () => seriesDisplayLabels(state.data, workspaceFiles),
    [state.data, workspaceFiles],
  );

  const changeSeriesColor = useDebouncedCallback(function setSeriesColor(
    seriesId: string,
    color: string,
  ) {
    vizStateDispatch(
      actions.setSeriesConfig(state.id, seriesId, { lineColor: color }),
    );
  }, 15);

  const removeSeries = (seriesId: string) => {
    vizStateDispatch(actions.removeSeriesFromPlotPanel(state.id, seriesId));
  };

  const setSeriesVisibility = (seriesId: string, visible: boolean) => {
    vizStateDispatch(actions.setSeriesVisibility(state.id, seriesId, visible));
  };

  if (state.data.length === 0 || !isLegendShown) {
    return null;
  }

  const MAX_VISIBLE_SERIES = 7;

  const series = state.data.slice(0, MAX_VISIBLE_SERIES);

  const legendPosition = state.config?.legendPosition ?? LegendPosition.TopLeft;
  const positionClass =
    positionClassMap[legendPosition] ??
    positionClassMap[LegendPosition.TopLeft];

  // Anchor both bottom legends relative to the plot area's bottom edge rather
  // than the panel floor: lift them by the chart's bottom (x-axis) pixel margin
  // on top of their base offset. This keeps them clear of the x-axis label band
  // and holds the two bottom corners at the same height. In the bottom-right
  // corner that same lift also clears the "Fit to Data" button, which is offset
  // off the panel bottom by the identical margin.
  const isBottomAnchored =
    legendPosition === LegendPosition.BottomRight ||
    legendPosition === LegendPosition.BottomLeft;
  const style =
    isBottomAnchored && chartExtents
      ? { bottom: `calc(var(--spacing-2xl) + ${chartExtents.pixel.bottom}px)` }
      : undefined;

  return (
    <Box
      className={classNames(styles.container, positionClass)}
      style={style}
      component="ul"
      bgcolor={(theme) => alpha(theme.palette.paper.main, 0.8)}
    >
      {series.map((seriesItem) => {
        const lineColor = seriesItem.config?.lineColor;
        const displayLabel = seriesLabels[seriesItem.id];
        return (
          <li className={styles.legendItem} key={seriesItem.id}>
            <div className={styles.legendItemLabel}>
              <input
                className={styles.legendItemColor}
                type="color"
                value={lineColor}
                name={seriesItem.id}
                onChange={(event) => {
                  changeSeriesColor(seriesItem.id, event.target.value);
                }}
              />
              <span
                role="button"
                tabIndex={0}
                aria-label="Toggle visibility of series"
                className={classNames(
                  "ph-no-capture",
                  styles.legendItemLabelText,
                  {
                    [styles.hidden]: !seriesItem.visible,
                  },
                )}
                onClick={function showOrHideSeries(event) {
                  // Prevent triggering the other chart onclick handlers
                  event.stopPropagation();

                  setSeriesVisibility(seriesItem.id, !seriesItem.visible);
                }}
              >
                {displayLabel}
              </span>
              {seriesItem.config?.transform && (
                <span className={styles.transformIndicator}>
                  <span className={styles.transformArrow}>→</span>
                  <span
                    className={styles.transformTag}
                    title={seriesItem.config.transform.expression}
                  >
                    𝑓(y)
                  </span>
                </span>
              )}
              <FallbackAlignmentIndicator
                visible={
                  alignment?.seriesById[seriesItem.id]?.isFallback ?? false
                }
                xAxisAlignment={state.config?.xAxisAlignment}
              />
            </div>
            <div>
              <OpenPanelSettings
                className={styles.iconButton}
                state={state}
                seriesId={seriesItem.id}
              />
              <IconButton
                className={styles.iconButton}
                onClick={function removeSeriesFromView() {
                  removeSeries(seriesItem.id);
                }}
              >
                <CloseIcon fontSize="inherit" />
              </IconButton>
            </div>
          </li>
        );
      })}
      <OverflowLegendItem
        state={state}
        visible={state.data.length > MAX_VISIBLE_SERIES}
      />
    </Box>
  );
}

interface OverflowLegendItemProps {
  state: PlotPanelState;
  visible: boolean;
}

/**
 * The last legend row when the panel has more series than the legend lists
 * individually. The folded series have no rows of their own, so this row's
 * settings button opens the panel settings sidebar on its Series section,
 * where every series is listed and configurable. No series is spotlighted:
 * this row stands for many, not one.
 */
function OverflowLegendItem({ state, visible }: OverflowLegendItemProps) {
  const workspace = useEphemeralWorkspaceState();
  const sidebar = useWorkspaceSidebar();

  if (!visible) {
    return null;
  }

  const openSeriesSettings = () => {
    workspace.selectPanel(state.id);
    sidebar.openView<PlotPanelSettingsSidebarProps>({
      type: SidebarViewType.PanelSettings,
      data: { field: "Series" },
    });
  };

  return (
    <li className={styles.legendItem}>
      <div className={styles.legendItemLabel}>...and others</div>
      <div>
        <IconButton
          aria-label="Open settings for all series"
          className={styles.iconButton}
          onClick={openSeriesSettings}
          size="small"
          title="Open settings for all series"
        >
          <SettingsIcon fontSize="inherit" />
        </IconButton>
      </div>
    </li>
  );
}

/**
 * Why the requested zero could not be found for a series. The substitute
 * basis is always the start of the series' recording, and a recording is a
 * workspace source: the files of one `?session=`, or a single file the user
 * picked on its own. These read "recording" rather than "session" so they
 * stay true of that second case.
 *
 * Exhaustive over both the unit and the session-anchor unions, mirroring
 * `resolveBasis` in `resolveSeriesAlignment`: adding a unit or anchor kind
 * leaves a path with no return and fails compilation here.
 */
function fallbackExplanation(alignment: XAxisAlignment): string {
  if (alignment.unit === "series") {
    return "This topic has no recorded start time inside its recording's range; the series is aligned to the start of that recording instead.";
  }
  const anchor = alignment.anchor;
  if (typeof anchor === "object") {
    return `Event "${anchor.eventName}" was not found for this series; it is aligned to the start of its recording instead.`;
  }
  switch (anchor) {
    case "first-data":
      return "No topic in this series' recording starts inside the recording's range; the series is aligned to the start of that recording instead.";
    case "session-start":
      return "This series could not be matched to a recording in this workspace; it is aligned to the start of its own topic data instead.";
  }
}

interface FallbackAlignmentIndicatorProps {
  visible: boolean;
  xAxisAlignment: XAxisAlignment | undefined;
}

/**
 * Marks a series that plots x = 0 at its recording's start because the zero
 * the panel's alignment asks for could not be found for it. The ⚠ glyph
 * matches the mark the plot tooltip puts on the same series.
 */
function FallbackAlignmentIndicator({
  visible,
  xAxisAlignment,
}: FallbackAlignmentIndicatorProps) {
  if (!visible || xAxisAlignment === undefined) {
    return null;
  }

  const explanation = fallbackExplanation(xAxisAlignment);
  return (
    <RobotoTooltip title={explanation}>
      <span
        className={styles.fallbackAlignmentTag}
        tabIndex={0}
        role="img"
        aria-label={explanation}
      >
        ⚠
      </span>
    </RobotoTooltip>
  );
}
